import React, { useState, useMemo, useCallback } from "react";
import AppDataGrid from "../../common/AppDataGrid";
import {
  CircularProgress,
  Box,
  Container,
  Snackbar,
  Alert,
} from "@mui/material";
import useFirestore from "../../../config/fireBaseHook";
import Add from "./inc/Add";
import Edit from "./inc/Edit";
import EditUpload from "../../../hooks/EditUpload";
import ClearCollectionButton from "../../common/ClearCollectionButton";
import TechPrepPortalMgtBreadCrumbs from "../../Layout/TechPrepPortalMgtBreadCrumbs";
import { useAuth } from "../../../config/AuthContext";
import SimpleToolbar from "../../common/SimpleToolbar";
import CustomFilterPanel from "../../common/CustomFilterPanel";
import useCustomFilter from "../../../hooks/useCustomFilter";
import { sitesFilters } from "../filterConfigurations";
import { formatDateTime } from "../../../utils/formatDate";

const Sites = () => {
  const { userPermissions } = useAuth();
  const canEdit = userPermissions["Sites"]?.edit;

  // Use Firestore hook, order by SiteID ascending. Collection name "Sites"
  const {
    data: sites,
    loading,
    error,
  } = useFirestore("Sites", "SiteID", "asc");
  const [selectedSite, setSelectedSite] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [sortModel, setSortModel] = useState([]);
  const [paginationModel, setPaginationModel] = useState({
    pageSize: 100,
    page: 0,
  });

  // Custom filter implementation
  const {
    filteredData,
    filterModel,
    setFilterModel,
    resetFilters,
    hasActiveFilters,
    isFiltering,
  } = useCustomFilter(sites || [], sitesFilters);

  const pageName = "Sites";

  const handleRowClick = (params) => {
    if (canEdit) {
      setSelectedSite(params.row);
      setEditDialogOpen(true);
    }
  };

  const handleDialogClose = () => {
    setEditDialogOpen(false);
    setSelectedSite(null);
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbarOpen(false);
  };

  const handleRecordUpdated = () => {
    setSnackbarMessage("Record has been updated successfully!");
    setSnackbarSeverity("success");
    setSnackbarOpen(true);
    setEditDialogOpen(false);
    // Data will refresh automatically due to onSnapshot in useFirestore
  };

  const handleRecordAdded = () => {
    // Data will refresh automatically due to onSnapshot in useFirestore
    setSnackbarMessage("Record has been added successfully!");
    setSnackbarSeverity("success");
    setSnackbarOpen(true);
  };

  const handleBulkActionComplete = (success, message) => {
    setSnackbarMessage(
      message ||
        (success ? "Bulk operation successful." : "Bulk operation failed."),
    );
    setSnackbarSeverity(success ? "success" : "error");
    setSnackbarOpen(true);
    // Data should refresh automatically via useFirestore's onSnapshot.
  };

  // Helper function to get display string for boolean-like values (0 for No, -1 for Yes)
  const getBooleanDisplayValue = (value) => {
    if (value === 0 || value === "0") {
      return "No";
    }
    if (value === -1 || value === "-1") {
      return "Yes";
    }
    if (value === null || typeof value === "undefined") {
      return "";
    }
    return String(value);
  };

  const columns = useMemo(
    () => [
      { field: "SiteID", headerName: "Site IRN", flex: 1, minWidth: 100 },
      {
        field: "Coordinator_Site",
        headerName: "Coordinator Site",
        flex: 1,
        minWidth: 140,
        renderCell: (params) => getBooleanDisplayValue(params.value),
      },
      { field: "SiteName", headerName: "Site Name", flex: 1, minWidth: 150 },
      {
        field: "SiteNameShort",
        headerName: "Site Name Short",
        flex: 1,
        minWidth: 140,
      },
      {
        field: "SiteAddress",
        headerName: "Site Address",
        flex: 1,
        minWidth: 150,
      },
      { field: "SiteCity", headerName: "Site City", flex: 1, minWidth: 120 },
      { field: "SiteState", headerName: "Site State", flex: 1, minWidth: 100 },
      { field: "SiteZip", headerName: "Site Zip", flex: 1, minWidth: 100 },
      { field: "SitePhone", headerName: "Site Phone", flex: 1, minWidth: 120 },
      { field: "SiteType", headerName: "Site Type", flex: 1, minWidth: 120 },
      { field: "Notes", headerName: "Notes", flex: 1, minWidth: 120 },
      {
        field: "updatedAt",
        headerName: "Updated At",
        flex: 1,
        minWidth: 160,
        renderCell: (params) => formatDateTime(params.value),
      },
    ],
    [],
  );

  // Prepare data for DataGrid
  const rows = useMemo(() => {
    if (!filteredData || !Array.isArray(filteredData)) return [];
    return filteredData.map((site, index) => ({
      id: site.id || index, // Use site.id if available, otherwise use index
      ...site,
    }));
  }, [filteredData]);

  // Simple toolbar without search (search is external)
  const CustomToolbar = useCallback(() => {
    return (
      <SimpleToolbar
        exportOptions={{
          csvOptions: {
            fileName: "sites",
            delimiter: ",",
          },
          printOptions: {
            hideFooter: true,
            hideToolbar: true,
          },
        }}
      />
    );
  }, []);

  const exportColumnOrder = columns.map((col) => col.field);

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
      >
        <p>
          Error fetching sites:{" "}
          {typeof error === "string"
            ? error
            : error?.message || "Unknown error"}
        </p>
      </Box>
    );
  }

  return (
    <Container maxWidth={false} sx={{ height: "100vh", paddingTop: "50px" }}>
      <div style={{ height: "100%", width: "100%" }}>
        <TechPrepPortalMgtBreadCrumbs pageName={pageName} icon="faCog" />

        <CustomFilterPanel
          filterConfig={sitesFilters}
          onFiltersChange={setFilterModel}
          initialFilters={filterModel}
          collapsible={true}
          title="Sites Filters"
          resultCount={filteredData?.length || 0}
          totalCount={sites?.length || 0}
          isSearching={isFiltering}
        />

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between", // Adjust as needed for layout
            alignItems: "center",
            mb: 2,
            flexWrap: "wrap", // Allow wrapping on smaller screens
          }}
        >
          {canEdit && <Add onRecordAdded={handleRecordAdded} />}
          {canEdit && (
            <EditUpload
              collectionName="Sites"
              onActionComplete={handleBulkActionComplete}
              exportColumnOrder={columns.map((col) => col.field)}
              exportColumnLabels={{ SiteID: "Site IRN" }}
              exportData={filteredData}
              primaryKeyField="SiteID"
              booleanFields={["Coordinator_Site"]}
              templateFields={exportColumnOrder}
            />
          )}
          {canEdit && <ClearCollectionButton collectionName="Sites" />}
        </Box>

        <Box sx={{ height: "70vh", width: "100%" }}>
          <AppDataGrid
            rows={rows}
            columns={columns}
            loading={loading}
            slots={{ toolbar: CustomToolbar }}
            sortModel={sortModel}
            onSortModelChange={setSortModel}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={[25, 50, 100, 200]}
            disableRowSelectionOnClick
            onRowClick={handleRowClick}
            sx={{
              "& .MuiDataGrid-row:hover": {
                cursor: canEdit ? "pointer" : "default",
              },
            }}
          />
        </Box>
      </div>
      {selectedSite && canEdit && (
        <Edit
          open={editDialogOpen}
          onClose={handleDialogClose}
          siteDetail={selectedSite} // Pass as siteDetail
          onRecordUpdated={handleRecordUpdated}
        />
      )}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
          variant="filled"
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default Sites;
